import type { Design } from "./types";
import type { Kit } from "./kit";
import {
  edgeLength,
  normalizeRailedEdges,
  perimeter,
  polygonArea,
} from "./geometry";
import { shoppingTotals, type ShoppingItem } from "./shoppingList";

const fmtUsd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const round1 = (n: number) => Math.round(n * 10) / 10;

export function buildDesignSummary(
  design: Design,
  kit: Kit,
  shoppingItems: ShoppingItem[],
): string {
  const { vertices, railHeightInches, color, stairs } = design;
  const perim = perimeter(vertices);
  const area = polygonArea(vertices);
  const railed = normalizeRailedEdges(design.railedEdges, vertices.length);

  const edgeLines = vertices
    .map((a, i) => {
      const b = vertices[(i + 1) % vertices.length];
      const len = edgeLength(a, b);
      const tags: string[] = [];
      if (stairs.enabled && stairs.edgeIndex === i) tags.push("stairs attached");
      if (!railed[i]) tags.push("NO RAILING (deck-only edge, e.g. against house)");
      const tag = tags.length > 0 ? ` (${tags.join("; ")})` : "";
      return `  Side ${i + 1}: ${round1(len)} ft${tag}`;
    })
    .join("\n");

  const stairLine = stairs.enabled
    ? `Stairs: on, side ${stairs.edgeIndex + 1}, ${round1(stairs.lengthFt)} ft run, railing ${stairs.withRailing ? "on" : "off"}`
    : "Stairs: off";

  const totals = shoppingTotals(shoppingItems);
  const itemLines = shoppingItems
    .map(
      (i) =>
        `  ${i.name}: ${i.qty} @ ${fmtUsd(i.price)} = ${fmtUsd(i.qty * i.price)}`,
    )
    .join("\n");

  return `Geometry:
  Sides: ${vertices.length}
  Perimeter: ${round1(perim)} ft
  Area: ${Math.round(area)} sq ft
${edgeLines}

Configuration:
  Rail height: ${railHeightInches}"
  Rail color: ${color}
  ${stairLine}

Cable & posts:
  Total railing run (perimeter${stairs.enabled && stairs.withRailing ? " + stairs" : ""}): ${round1(kit.totalRunFt)} ft
  Separate runs: ${kit.runs}
  Cables per run: ${kit.cablesPerRun} (rail height ${railHeightInches}")
  Total cable needed: ${Math.round(kit.cableFeet)} ft
  Tensioner sets: ${kit.tensionerSets}
  Posts: ${kit.posts} total
    Level Corner: ${kit.postBreakdown.levelCorner}
    Level Line: ${kit.postBreakdown.levelLine}
    Level End: ${kit.postBreakdown.levelEnd}
    Angle End: ${kit.postBreakdown.angleEnd}
    Angle Line: ${kit.postBreakdown.angleLine}

Shopping list (current prices shown to user):
${itemLines}
  --
  Items: ${totals.qty}
  Total: ${fmtUsd(totals.price)}`;
}
